import React, { useState } from "react";
import { Crown, Check, Globe2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, SectionLabel } from "./Primitives";

const PREMIUM_FEATURES = ["Full Lessons library", "Quizzes with instant results", "AI-generated Assignments & worksheets", "The AI Tutor, trained on African history", "Certificates of achievement"];

export function Paywall({ compact = false }) {
  const { user, login } = useAuth();
  const [starting, setStarting] = useState(false);

  const unlock = async () => {
    if (!user) {
      login();
      return;
    }
    setStarting(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setStarting(false);
    }
  };

  return (
    <div className={compact ? "" : "mx-auto max-w-2xl px-6 py-16 md:px-12"} data-testid="paywall">
      <div className="border border-gold/60 bg-ash p-8 md:p-10">
        <SectionLabel>Premium Access</SectionLabel>
        <div className="mt-2 flex items-center gap-3">
          <Crown className="h-8 w-8 text-gold" />
          <h2 className="font-serif text-3xl font-semibold text-bone md:text-4xl">Unlock the full curriculum</h2>
        </div>
        <p className="mt-4 text-muted">
          Support Our African Narratives and open every learning tool for your family or classroom.
        </p>
        <div className="mt-6 flex items-baseline gap-2">
          <span className="font-serif text-5xl text-gold" data-testid="paywall-price">$3</span>
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted">/ month</span>
        </div>
        <ul className="mt-6 space-y-3">
          {PREMIUM_FEATURES.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-bone">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {f}
            </li>
          ))}
        </ul>
        <button
          onClick={unlock}
          disabled={starting}
          data-testid="paywall-unlock-btn"
          className="mt-8 flex w-full items-center justify-center gap-2 border border-gold bg-gold px-6 py-3 font-mono text-sm uppercase tracking-[0.15em] text-ink transition-colors hover:bg-transparent hover:text-gold disabled:opacity-60"
        >
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
          {user ? "Unlock Premium — $3/month" : "Sign in to unlock — $3/month"}
        </button>
        <p className="mt-5 flex items-start gap-2 text-xs text-muted" data-testid="paywall-free-note">
          <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" />
          The Globe, Dispersion, Stories, Timeline and Kingdoms always stay free for everyone.
        </p>
      </div>
    </div>
  );
}

export function PremiumGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (user?.is_premium) return children;
  return <Paywall />;
}
